import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowRight, CheckCircle } from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import Seo from '../../components/common/Seo';

export default function Apply() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!email) return;
    setError('');

    setIsLoading(true);
    const res = await fetch('/api/apply', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    if (res.ok) {
      setIsLoading(false);
      setIsSubmitted(true);
      setEmail('');
      // Reset after 3 seconds
      setTimeout(() => setIsSubmitted(false), 3000);
      return;
    }

    const body = await res.json().catch(() => ({}));
    setError(body.error || 'Could not submit application. Try again.');
    setIsLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-game-black text-white flex flex-col">
        <Seo
          title="Apply"
          description="Apply to join GameMeetsWorld updates and creator opportunities."
          path="/apply"
          breadcrumbs={[
            { name: 'Home', path: '/' },
            { name: 'Apply', path: '/apply' }
          ]}
        />
        <Navbar mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />

        <main className="flex-grow flex items-center justify-center px-6 py-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2 }}
              className="inline-block mb-6"
            >
              <div className="w-20 h-20 bg-accent-green/20 rounded-full flex items-center justify-center border-2 border-accent-green">
                <CheckCircle className="w-10 h-10 text-accent-green" />
              </div>
            </motion.div>

            <h1 className="text-5xl font-bold mb-4">
              <span className="text-accent-green">Thank You!</span>
            </h1>
            <p className="text-text-secondary text-lg mb-8 max-w-2xl">
              We've got your email. We'll be in touch soon!
            </p>

            <Link
              to="/"
              className="inline-block px-8 py-3 bg-accent-green text-black font-bold rounded-lg hover:shadow-lg shadow-accent-green/50 transition-all"
            >
              Back to Home
            </Link>
          </motion.div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-game-black text-white flex flex-col">
      <Seo
        title="Apply"
        description="Apply to join GameMeetsWorld updates and creator opportunities."
        path="/apply"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Apply', path: '/apply' }
        ]}
      />
      <Navbar mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />

      <main className="flex-grow pt-32 pb-20 relative overflow-hidden">
        {/* Background Tech Grid & Glow */}
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(161, 255, 79, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(161, 255, 79, 0.05) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
          aria-hidden="true"
        />
        <div
          className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-accent-green/5 blur-[100px] rounded-full pointer-events-none"
          aria-hidden="true"
        />

        <div className="max-w-3xl mx-auto px-6 lg:px-12 relative z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-16 text-center"
          >
            <h1 className="text-[clamp(3.5rem,6vw,7rem)] font-black uppercase italic tracking-tighter leading-none mb-6 text-white">
              Want to <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-green via-green-400 to-accent-green py-2 inline-block">
                Apply?
              </span>
            </h1>
            <p className="text-xl text-white/70 max-w-2xl mx-auto font-light tracking-wide leading-relaxed">
              Join GameMeetsWorld and be part of our community.
            </p>
          </motion.div>

          {/* Email Form */}
          <motion.div
            className="max-w-lg mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex flex-col sm:flex-row gap-0 bg-[#0F1419] border border-white/10 p-1 relative group focus-within:border-accent-green/50 transition-colors duration-300">

              {/* Input Field */}
              <div className="flex-1 flex items-center px-6 py-5">
                <Mail
                  className="w-5 h-5 text-white/60 mr-4 group-focus-within:text-accent-green transition-colors"
                  aria-hidden="true"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="YOUR EMAIL ADDRESS"
                  className="w-full bg-transparent text-white placeholder-white/40 font-mono text-sm focus:outline-none uppercase tracking-widest"
                  disabled={isLoading}
                  aria-label="Email address to apply"
                />
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={isLoading || !email}
                className="relative px-8 py-5 bg-accent-green hover:bg-[#8FE639] transition-colors overflow-hidden min-w-[180px] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-accent-green focus:ring-offset-2 focus:ring-offset-[#021206]"
                aria-label="Submit application"
              >
                <span className="relative z-10 font-black uppercase tracking-wider flex items-center justify-center gap-2 text-black text-sm">
                  {isLoading ? (
                    <>
                      <span className="animate-pulse">Sending...</span>
                    </>
                  ) : (
                    <>
                      Apply Now <ArrowRight className="w-5 h-5" aria-hidden="true" />
                    </>
                  )}
                </span>
              </button>
            </div>

            {/* Privacy Note */}
            <p className="text-xs text-white/50 mt-4 font-light">
              We'll review your application and get back to you within 48 hours.
            </p>
            {error && <p className="text-sm text-red-300 mt-3">{error}</p>}
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
